import { computed, signal } from '@angular/core';
import type {
  SoftphoneParticipantMutationResponseDto,
  SoftphoneParticipantSnapshotDto,
} from 'voxr-shared';
import {
  hasSoftphoneParticipantSnapshot,
  mapSoftphoneParticipants,
  normalizeSoftphoneParticipantStatus,
} from './softphone-participants.mapper';
import type { SoftphoneParticipantRow } from './softphone-participants.mapper';

export type SoftphoneConferenceContext = {
  callId?: string;
  activityId?: string;
  agentCallSid?: string;
  inferEndedAt?: boolean;
  mainStartedAt?: number;
  thirdStartedAt?: number;
  mainStatus?: string;
};

type ParticipantAction = 'mute' | 'unmute' | 'hold' | 'unhold';

export type SoftphoneConferenceApi = {
  getParticipants(callId: string, activityId?: string): Promise<SoftphoneParticipantSnapshotDto>;
  participantAction(payload: {
    callId: string;
    participantCallSid: string;
    action: ParticipantAction;
    activityId?: string;
  }): Promise<SoftphoneParticipantMutationResponseDto>;
  endParticipant(payload: {
    callId: string;
    participantCallSid: string;
    activityId?: string;
  }): Promise<SoftphoneParticipantMutationResponseDto>;
  endConference(callId: string, activityId?: string): Promise<SoftphoneParticipantMutationResponseDto>;
};

type SoftphoneConferenceSessionOptions = {
  context: () => SoftphoneConferenceContext;
  rowsApplied?: (rows: SoftphoneParticipantRow[]) => void;
};

export class SoftphoneConferenceSession {
  readonly rows = signal<SoftphoneParticipantRow[]>([]);
  readonly loading = signal(false);
  readonly pendingParticipantCallSid = signal<string | undefined>(undefined);
  readonly ending = signal(false);
  readonly mutationPending = computed(() => !!this.pendingParticipantCallSid() || this.ending());
  private readonly dismissedThirdCallSids = signal<ReadonlySet<string>>(new Set());
  readonly thirdParticipantLeg = computed(() => {
    const rows = this.rows().filter(row => row.legType === 'third');
    const dismissed = this.dismissedThirdCallSids();
    const active = rows.find(row => !dismissed.has(row.callSid) && !this.isThirdParticipantEnded(row));
    if (active) return active;
    const latest = rows[rows.length - 1];
    return latest && !dismissed.has(latest.callSid) ? latest : undefined;
  });
  readonly thirdParticipantEnded = computed(() => this.isThirdParticipantEnded(this.thirdParticipantLeg()));

  private refreshPromise?: Promise<boolean>;
  private generation = 0;
  private contextKey?: string;

  constructor(
    private readonly api: SoftphoneConferenceApi,
    private readonly options: SoftphoneConferenceSessionOptions,
  ) {}

  isThirdParticipantEnded(row?: SoftphoneParticipantRow) {
    return !!row && (!!row.endedAt || row.status === 'ended');
  }

  dismissEndedThirdParticipant(callSid: string) {
    const context = this.currentContext();
    const selected = this.thirdParticipantLeg();
    if (!context.callId || !selected || selected.callSid !== callSid
      || !this.isThirdParticipantEnded(selected) || this.mutationPending()
      || this.rows().some(row => row.legType === 'third' && !this.isThirdParticipantEnded(row))) return false;
    this.dismissedThirdCallSids.update(current => new Set([...current, callSid]));
    return true;
  }

  applySnapshot(payload?: SoftphoneParticipantSnapshotDto) {
    if (!hasSoftphoneParticipantSnapshot(payload)) return false;

    const context = this.currentContext();
    const mappedRows = mapSoftphoneParticipants({
      children: payload?.threeWayChildren || [],
      participants: payload?.participants || [],
      existingRows: this.rows(),
      agentCallSid: context.agentCallSid,
      inferEndedAt: context.inferEndedAt,
    });
    const mainStatus = context.mainStatus
      ? normalizeSoftphoneParticipantStatus(context.mainStatus)
      : undefined;
    const rows = mappedRows.map(row => {
      if (row.legType === 'main') {
        return {
          ...row,
          status: row.status === '--' && mainStatus ? mainStatus : row.status,
          startedAt: row.startedAt || context.mainStartedAt,
        };
      }
      if (row.legType === 'third') {
        return { ...row, startedAt: row.startedAt || context.thirdStartedAt };
      }
      return row;
    });

    this.rows.set(rows);
    this.options.rowsApplied?.(rows);
    return true;
  }

  applyHold(participantCallSid: string, onHold: boolean) {
    if (!participantCallSid) return;
    this.rows.update(rows => rows.map(row =>
      row.callSid === participantCallSid ? { ...row, onHold } : row
    ));
  }

  refresh() {
    const context = this.currentContext();
    if (this.refreshPromise) return this.refreshPromise;
    if (!context.callId) return Promise.resolve(false);

    this.loading.set(true);
    const generation = this.generation;
    const refresh = this.api.getParticipants(context.callId, context.activityId)
      .then(payload => {
        if (!this.isCurrent(context, generation)) {
          this.currentContext();
          return false;
        }
        return this.applySnapshot(payload);
      })
      .catch(error => {
        if (!this.isCurrent(context, generation)) {
          this.currentContext();
          return false;
        }
        throw error;
      })
      .finally(() => {
        if (generation === this.generation) this.loading.set(false);
        if (this.refreshPromise === refresh) this.refreshPromise = undefined;
      });
    this.refreshPromise = refresh;
    return refresh;
  }

  reconcile(payload?: SoftphoneParticipantSnapshotDto) {
    if (this.applySnapshot(payload)) return Promise.resolve(true);
    return this.refresh();
  }

  async act(participantCallSid: string, action: ParticipantAction) {
    if (this.mutationPending()) throw new Error('A conference action is already in progress.');
    const context = this.requireContext();
    const generation = this.generation;
    this.pendingParticipantCallSid.set(participantCallSid);
    try {
      const response = await this.api.participantAction({
        callId: context.callId,
        participantCallSid,
        action,
        activityId: context.activityId,
      });
      if (this.isCurrent(context, generation)) this.applySnapshot(response);
      else this.currentContext();
      return response;
    } finally {
      if (this.isCurrent(context, generation)) this.pendingParticipantCallSid.set(undefined);
      else this.currentContext();
    }
  }

  async remove(participantCallSid: string) {
    if (this.mutationPending()) throw new Error('A conference action is already in progress.');
    const context = this.requireContext();
    const generation = this.generation;
    this.pendingParticipantCallSid.set(participantCallSid);
    try {
      const response = await this.api.endParticipant({
        callId: context.callId,
        participantCallSid,
        activityId: context.activityId,
      });
      if (this.isCurrent(context, generation)) this.applySnapshot(response);
      else this.currentContext();
      return response;
    } finally {
      if (this.isCurrent(context, generation)) this.pendingParticipantCallSid.set(undefined);
      else this.currentContext();
    }
  }

  async endAll() {
    if (this.mutationPending()) throw new Error('A conference action is already in progress.');
    const context = this.requireContext();
    const generation = this.generation;
    this.ending.set(true);
    try {
      const response = await this.api.endConference(context.callId, context.activityId);
      if (this.isCurrent(context, generation)) {
        this.applySnapshot(response);
        this.rows.set([]);
        this.options.rowsApplied?.([]);
      }
      else this.currentContext();
      return response;
    } finally {
      if (this.isCurrent(context, generation)) this.ending.set(false);
      else this.currentContext();
    }
  }

  reset() {
    this.generation += 1;
    this.rows.set([]);
    this.dismissedThirdCallSids.set(new Set());
    this.loading.set(false);
    this.pendingParticipantCallSid.set(undefined);
    this.ending.set(false);
    this.refreshPromise = undefined;
    this.contextKey = undefined;
    this.options.rowsApplied?.([]);
  }

  dispose() {
    this.reset();
  }

  private requireContext() {
    const context = this.currentContext();
    if (!context.callId) throw new Error('Active call context is unavailable.');
    return { ...context, callId: context.callId };
  }

  private captureContext() {
    const context = this.options.context();
    return {
      ...context,
      callId: context.callId?.toString().trim() || undefined,
      activityId: context.activityId?.toString().trim() || undefined,
      agentCallSid: context.agentCallSid?.toString().trim() || undefined,
    };
  }

  private currentContext() {
    const context = this.captureContext();
    const key = [context.callId || '', context.activityId || '', context.agentCallSid || ''].join('|');
    if (this.contextKey !== undefined && this.contextKey !== key) {
      this.generation += 1;
      this.rows.set([]);
      this.dismissedThirdCallSids.set(new Set());
      this.loading.set(false);
      this.pendingParticipantCallSid.set(undefined);
      this.ending.set(false);
      this.refreshPromise = undefined;
      this.options.rowsApplied?.([]);
    }
    this.contextKey = key;
    return context;
  }

  private isCurrent(context: ReturnType<SoftphoneConferenceSession['captureContext']>, generation: number) {
    if (generation !== this.generation) return false;
    const current = this.captureContext();
    return current.callId === context.callId
      && current.activityId === context.activityId
      && current.agentCallSid === context.agentCallSid;
  }
}
